/**
 * Moving quietly (M5-03).
 *
 * The one genuinely new mechanic in the lesser-hunts ticket, and it is kept as
 * small as the ticket asks: a stance, a speed multiplier, and a shorter range at
 * which eyes pick you up. There is no stealth meter, no detection roll and no
 * hidden state — a player is either creeping or they are not, and everything
 * that follows from it is arithmetic somebody can predict.
 *
 * Three rules, and each of them is a refusal to build the obvious bigger thing:
 *
 * 1. **It halves your pace** (`SNEAK_SPEED`). A stalk costs time, and time is
 *    the only currency the valley has that a player cannot forage more of.
 * 2. **It halves what sees you and nothing that smells you**
 *    (`SNEAK_SIGHT_FACTOR`). Creeping upwind of a stag still fails. M2-05's
 *    lesson survives the addition of stealth, which was the risk.
 * 3. **Noise breaks it.** Swinging at something, being hit, or getting a body
 *    onto your shoulder all drop the stance, because all three are loud. That
 *    is why this module exists at all rather than being three lines in
 *    `combat.ts`: the *rule* is "noise ends a stalk", and it wants one home.
 */
import { parseSneak } from './actions.js';
/**
 * Stop creeping, if they were.
 *
 * Called from wherever somebody makes a noise. Takes the combatant rather than
 * an id so the caller can hand over whatever it already has in scope, and does
 * nothing at all when the stance is already down — a man who was walking
 * normally does not get an event saying he has stopped sneaking.
 */
export function breakStalk(ctx, actorId, combatant, why) {
    if (!combatant?.sneaking)
        return;
    combatant.sneaking = false;
    const actor = ctx.world.actors[actorId];
    ctx.emit({
        type: 'status',
        actors: [actorId],
        refs: ['sneak', 'broken', why],
        text: `${actor?.name ?? actorId} is not moving quietly any more.`,
        // Archived: the player already knows why, because they just did it. What
        // this is for is the log telling them afterwards why the stag ran.
        audience: 'archive',
        ...(actor ? { at: { x: actor.x, y: actor.y } } : {}),
    });
}
export const sneakHandler = {
    type: 'sneak',
    parse: parseSneak,
    check(intent, ctx) {
        if (!ctx.world.actors[intent.actorId])
            return `${intent.actorId} is not in the world`;
        const combatant = ctx.world.combat[intent.actorId];
        if (!combatant)
            return `${intent.actorId} has no body to move with`;
        if (combatant.vitals.dead)
            return `${intent.actorId} is dead`;
        // Nothing else refuses it, deliberately. A man with a broken leg creeping
        // is a man crawling, and the speed rules already say what that costs him —
        // a capability gate here would be the interface deciding he may not try.
        return null;
    },
    apply(intent, ctx) {
        const actor = ctx.world.actors[intent.actorId];
        const combatant = ctx.world.combat[intent.actorId];
        if (!actor || !combatant)
            return;
        if ((combatant.sneaking ?? false) === intent.payload.on)
            return;
        combatant.sneaking = intent.payload.on;
        ctx.emit({
            type: 'status',
            actors: [actor.id],
            refs: ['sneak', intent.payload.on ? 'on' : 'off'],
            text: intent.payload.on
                ? `${actor.name} drops into a crouch and moves quietly.`
                : `${actor.name} straightens up.`,
            audience: 'archive',
            at: { x: actor.x, y: actor.y },
        });
    },
};
export const STALK_INTENTS = [sneakHandler];
//# sourceMappingURL=stalk.js.map